import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Check } from "lucide-react";

const POINTS = [
  "Continuous XAU/USD monitoring against your plan",
  "Deterministic risk & position sizing",
  "Setup quality score with full breakdown",
  "Paper trading, journal & AI coaching",
];

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success("Account created. Let's build your trading plan.");
      nav("/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bg)] text-[var(--text)]">
      <div className="flex items-center justify-center p-6 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="text-2xl font-bold tracking-tight">Start paper trading free</h1>
          <p className="mt-1 text-sm text-[var(--text-sec)]">No credit card. Free tier during launch.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            {["name", "email", "password"].map((f) => (
              <div key={f}>
                <label className="text-sm text-[var(--text-sec)] capitalize">{f}</label>
                <input data-testid={`signup-${f}`} type={f === "password" ? "password" : f === "email" ? "email" : "text"}
                  required value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2.5 text-sm outline-none focus:border-aurum transition-colors" />
              </div>
            ))}
            <button data-testid="signup-submit" disabled={loading}
              className="btn-gold w-full rounded-lg py-2.5 text-sm font-semibold transition-all hover:brightness-110 disabled:opacity-60">
              {loading ? "Creating…" : "Create account"}
            </button>
          </form>
          <p className="mt-6 text-sm text-[var(--text-sec)]">
            Already have an account? <Link to="/login" className="text-aurum hover:underline" data-testid="to-login">Log in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden grain border-l border-[var(--border2)] order-1 lg:order-2"
        style={{ background: "radial-gradient(circle at 80% 20%, rgba(214,174,87,0.10), transparent 55%), #0D1117" }}>
        <Link to="/" className="self-end"><Logo size={30} /></Link>
        <div>
          <h2 className="text-3xl font-bold tracking-tight max-w-sm">Your strategy. Monitored continuously.</h2>
          <div className="mt-6 space-y-3">
            {POINTS.map((p) => (
              <div key={p} className="flex items-center gap-3 text-sm text-[var(--text-sec)]">
                <span className="h-5 w-5 rounded-full flex items-center justify-center" style={{ background: "rgba(33,197,142,0.15)" }}>
                  <Check size={12} className="text-bull" />
                </span>
                {p}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-[var(--text-muted)]">Trading involves substantial risk. No guarantees of profit.</p>
      </div>
    </div>
  );
}
